'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Cpu, Paintbrush, Rocket, RefreshCw } from 'lucide-react';

const features = [
  { Icon: Cpu, title: 'AI-Native dari Hari Pertama', description: 'Setiap karya kami dirancang dengan AI sebagai fondasi. Hasilnya: produk digital yang lebih cerdas dan terus berevolusi.' },
  { Icon: Paintbrush, title: 'Kreativitas Tanpa Batas', description: 'Para creator kami menggabungkan seni desain dengan kekuatan teknologi. Setiap proyek adalah karya yang unik.' },
  { Icon: Rocket, title: 'Eksekusi Cepat & Terstruktur', description: 'Proses transparan dengan timeline jelas. Sprint 2 minggu, review reguler, dan delivery tepat waktu.' },
  { Icon: RefreshCw, title: 'Automation-First Thinking', description: 'Kami tidak hanya membangun, kami mengotomasi. Setiap produk dilengkapi sistem automation yang siap pakai.' },
];

const stats = [
  { label: 'Proyek Diselesaikan', sub: 'Dari berbagai industri', num: 50, suffix: '+' },
  { label: 'Lebih Cepat', sub: 'Dibanding metode konvensional', num: 3, suffix: '×' },
  { label: 'Uptime Dijamin', sub: 'SLA enterprise-grade', num: 99, suffix: '%' },
];

function Counter({ target, suffix, duration = 1600 }: { target: number; suffix: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const hasRun = useRef(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasRun.current) {
        hasRun.current = true;
        const start = performance.now();
        const animate = (now: number) => {
          const p = Math.min((now - start) / duration, 1);
          setCount(Math.round((1 - Math.pow(1 - p, 3)) * target));
          if (p < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);
  return <span ref={ref} className="tabular-nums">{count}{suffix}</span>;
}

function BentoCard({ children, span }: { children: React.ReactNode; span: string }) {
  return (
    <div className={`${span} bento-cell opacity-0`}>
      <div className="spotlight-card rounded-2xl p-[1px] h-full" style={{ background: 'radial-gradient(circle, rgb(24,24,27) 0%, rgb(0,0,0) 100%)' }}>
        <div className="rounded-[15px] bg-[rgba(15,15,18,0.9)] h-full overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function KeunggulanSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const bentoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ctx: { revert: () => void };
    const init = async () => {
      const { gsap } = await import('gsap');
      const { ScrollTrigger } = await import('gsap/ScrollTrigger');
      gsap.registerPlugin(ScrollTrigger);
      ctx = gsap.context(() => {
        gsap.fromTo(titleRef.current, { opacity: 0, y: 32, filter: 'blur(8px)' },
          { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.8, ease: 'power2.out', scrollTrigger: { trigger: sectionRef.current, start: 'top 75%' } });
        const cells = bentoRef.current?.querySelectorAll('.bento-cell');
        if (cells) gsap.fromTo(cells, { opacity: 0, y: 40, filter: 'blur(6px)' },
          { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.8, stagger: 0.12, ease: 'power2.out', scrollTrigger: { trigger: bentoRef.current, start: 'top 78%' } });
      }, sectionRef);
    };
    init();
    return () => { if (ctx) ctx.revert(); };
  }, []);

  const IconBox = ({ icon: Ic }: { icon: React.ElementType }) => (
    <div className="w-11 h-11 rounded-md border border-white/10 bg-white/[0.04] flex items-center justify-center flex-shrink-0">
      <Ic size={20} className="text-lime-500" />
    </div>
  );

  return (
    <section id="keunggulan" ref={sectionRef} className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div ref={titleRef} className="opacity-0 mb-16 md:mb-20">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-lime-500" />
            <span className="text-lime-500 text-[11px] font-semibold tracking-[0.2em] uppercase">Kenapa STUDO.ID</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-light text-white tracking-tight leading-[1.1] max-w-2xl">
            Lebih dari Sekadar{' '}
            <span className="font-semibold" style={{ background: 'linear-gradient(135deg, #a3e635, #84cc16)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Studio Biasa</span>
          </h2>
        </div>

        <div ref={bentoRef} className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          <BentoCard span="md:col-span-8">
            <div className="p-8 md:p-10 min-h-[220px] flex flex-col justify-between">
              <div className="flex items-start gap-5">
                <IconBox icon={features[0].Icon} />
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg md:text-xl font-semibold text-white tracking-tight">{features[0].title}</h3>
                  <p className="text-zinc-400 text-[14px] leading-relaxed max-w-lg">{features[0].description}</p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-6 gap-1.5 opacity-20">
                {Array.from({ length: 18 }).map((_, i) => (
                  <div key={i} className="h-1.5 rounded-full" style={{ background: i % 3 === 0 ? '#a3e635' : 'rgba(255,255,255,0.3)', opacity: 0.4 + (i % 4) * 0.15 }} />
                ))}
              </div>
            </div>
          </BentoCard>

          <BentoCard span="md:col-span-4">
            <div className="p-7 min-h-[220px] flex flex-col gap-5">
              <IconBox icon={features[1].Icon} />
              <h3 className="text-lg font-semibold text-white tracking-tight">{features[1].title}</h3>
              <p className="text-zinc-400 text-[13px] leading-relaxed">{features[1].description}</p>
            </div>
          </BentoCard>

          <BentoCard span="md:col-span-4">
            <div className="p-7 min-h-[220px] flex flex-col gap-5">
              <IconBox icon={features[2].Icon} />
              <h3 className="text-lg font-semibold text-white tracking-tight">{features[2].title}</h3>
              <p className="text-zinc-400 text-[13px] leading-relaxed">{features[2].description}</p>
            </div>
          </BentoCard>

          <BentoCard span="md:col-span-8">
            <div className="p-8 md:p-10 min-h-[220px] flex flex-col justify-between">
              <div className="flex items-start gap-5">
                <IconBox icon={features[3].Icon} />
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg md:text-xl font-semibold text-white tracking-tight">{features[3].title}</h3>
                  <p className="text-zinc-400 text-[14px] leading-relaxed max-w-lg">{features[3].description}</p>
                </div>
              </div>
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                {['Input', '→', 'Process', '→', 'Output', '→', 'Notify'].map((step, i) => (
                  <span key={i} className={`text-[11px] font-semibold tracking-wide ${step === '→' ? 'text-lime-500/60' : 'px-3 py-1 rounded-full border border-white/[0.08] bg-white/[0.03] text-zinc-400'}`}>
                    {step}
                  </span>
                ))}
              </div>
            </div>
          </BentoCard>

          <BentoCard span="md:col-span-12">
            <div className="px-8 md:px-12 py-8 grid grid-cols-3 gap-6 md:gap-0 md:divide-x divide-white/[0.06]">
              {stats.map(s => (
                <div key={s.label} className="flex flex-col items-center gap-2 md:px-8">
                  <span className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                    <Counter target={s.num} suffix={s.suffix} />
                  </span>
                  <span className="text-[13px] font-semibold text-white/80 text-center">{s.label}</span>
                  <span className="text-[11px] text-zinc-500 text-center">{s.sub}</span>
                </div>
              ))}
            </div>
          </BentoCard>
        </div>
      </div>
    </section>
  );
}
